import React, { useState, useRef } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import Lightbox from '../components/Lightbox'

const LAYOUT = [
  { col: 'span 2', row: 'span 2', type: 'large' }, // 0
  { col: 'span 1', row: 'span 1', type: 'sm'    }, // 1
  { col: 'span 1', row: 'span 1', type: 'sm'    }, // 2
  { col: 'span 1', row: 'span 1', type: 'sm'    }, // 3
  { col: 'span 1', row: 'span 1', type: 'sm'    }, // 4
  { col: 'span 1', row: 'span 1', type: 'sm'    }, // 5
  { col: 'span 1', row: 'span 1', type: 'sm'    }, // 6
  { col: 'span 1', row: 'span 1', type: 'sm'    }, // 7
  { col: 'span 1', row: 'span 1', type: 'sm'    }, // 8
  { col: 'span 1', row: 'span 1', type: 'sm'    }, // 9
  { col: 'span 1', row: 'span 1', type: 'sm'    }, // 10
  { col: 'span 2', row: 'span 2', type: 'large' }, // 11
  { col: 'span 1', row: 'span 1', type: 'sm'    }, // 12
  { col: 'span 1', row: 'span 1', type: 'sm'    }, // 13
]

const MAX = 14
const MOCK_COLORS = [
  '#c8d8e4','#b8ccd8','#a8c0cc','#d4e0e8',
  '#bcccd8','#c0d0dc','#b4c8d4','#ccdae4',
  '#b0c4d0','#c4d4e0','#bcccd8','#c8d8e4',
  '#a8c0cc','#b8ccd8',
]

function MobileCarousel({ images, onOpen }) {
  const [idx, setIdx] = useState(0)
  const total = images.length
  const prev = () => setIdx(i => (i - 1 + total) % total)
  const next = () => setIdx(i => (i + 1) % total)

  const touchStart = useRef(null)
  const onTouchStart = e => { touchStart.current = e.touches[0].clientX }
  const onTouchEnd   = e => {
    if (touchStart.current === null) return
    const dx = e.changedTouches[0].clientX - touchStart.current
    if (dx >  40) prev()
    if (dx < -40) next()
    touchStart.current = null
  }

  if (!total) return (
    <div className="sc-gallery-carousel__empty">
      {MOCK_COLORS.slice(0, 6).map((c, i) => (
        <div key={i} className="sc-gallery-carousel__mock" style={{ background: c }} />
      ))}
    </div>
  )

  return (
    <div className="sc-gallery-carousel" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div
        className="sc-gallery-carousel__track"
        style={{ transform: `translateX(${-idx * 100}%)` }}
      >
        {images.map((src, i) => (
          <div key={i} className="sc-gallery-carousel__slide" onClick={() => onOpen(i)}>
            <img src={src} alt={`Gallery ${i + 1}`} loading="lazy" />
          </div>
        ))}
      </div>

      {total > 1 && (
        <>
          <button className="sc-gallery-carousel__btn sc-gallery-carousel__btn--prev" onClick={prev} aria-label="Previous">
            <ChevronLeft size={18} strokeWidth={1.5} />
          </button>
          <button className="sc-gallery-carousel__btn sc-gallery-carousel__btn--next" onClick={next} aria-label="Next">
            <ChevronRight size={18} strokeWidth={1.5} />
          </button>
          <div className="sc-gallery-carousel__counter">{idx + 1} / {total}</div>
        </>
      )}
    </div>
  )
}

export default function Gallery({ images }) {
  const [lightboxIdx, setLightboxIdx] = useState(null)
  const realImages = images?.length ? images.slice(0, MAX) : []

  const slots = Array.from({ length: MAX }, (_, i) => ({
    src:   realImages[i] ?? null,
    real:  i < realImages.length,
    color: MOCK_COLORS[i],
    layout: LAYOUT[i],
  }))

  return (
    <>
      <div className="sc-gallery">
        <div className="sc-gallery__header">
          <h2 className="ljn-section-title">Gallery</h2>
          <div className="ljn-divider" />
        </div>

        {/* Mobile: carousel */}
        <div className="sc-gallery--mobile">
          <MobileCarousel images={realImages} onOpen={setLightboxIdx} />
        </div>

        {/* Tablet+: grid */}
        <div className="sc-gallery--grid">
          <div className="sc-gallery__grid">
            {slots.map((slot, i) => (
              <div
                key={i}
                className={`sc-gallery__item sc-gallery__item--${slot.layout.type} ${!slot.real ? 'sc-gallery__item--placeholder' : ''}`}
                style={{
                  gridColumn: slot.layout.col,
                  gridRow:    slot.layout.row,
                  background: !slot.real ? slot.color : undefined,
                }}
                onClick={() => slot.real && setLightboxIdx(i < realImages.length ? i : 0)}
              >
                {slot.src
                  ? <img src={slot.src} alt={`Gallery ${i + 1}`} loading="lazy" />
                  : <div className="sc-gallery__mock-label">Photo {i + 1}</div>
                }
              </div>
            ))}
          </div>
        </div>
      </div>

      {lightboxIdx !== null && (
        <Lightbox
          images={realImages.length ? realImages : []}
          startIdx={lightboxIdx}
          caption='Gallery'
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </>
  )
}
